import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    AccountSummaryModelDto,
    AccountTransactionsClient,
    CheckimagesClient,
    CompanyAccountModelDto,
    CompanyAccountsClient,
    DashboardClient,
    ResearchTransactionFilterModelDto,
    ResearchTransactionsClient,
    TransactionCodeGroupClient,
} from '@treasury/api/channel';
import { TmHttpClient } from '@treasury/core/http';
import { getDateRange } from '@treasury/utils';
import { CompanyAccount } from '../company/company-account.entity';
import { Account } from './types';
import { CheckImage } from './types/check-image.entity';
import { LoanAccountDetail } from './types/loan-account-details.entity';
import { ResearchedTransaction } from './types/researched-transaction.entity';
import { TimeDepositAccountDetail } from './types/time-deposit-account-details.entity';

export type TransactionCode = {
    name: string;
    isReadOnly: boolean;
    id: number;
    updatedBy: string;
    updatedDate: string;
};

@Injectable()
export class AccountsService {
    constructor(
        private readonly transactionsClient: AccountTransactionsClient,
        private readonly checkImagesClient: CheckimagesClient,
        private readonly researchTransactionsClient: ResearchTransactionsClient,
        private readonly transactionCodeGroupClient: TransactionCodeGroupClient,
        private readonly dashboardClient: DashboardClient,
        private readonly companyAccountsClient: CompanyAccountsClient
    ) {}

    public async getAccounts() {
        const responses = await Promise.all(
            ['Deposit', 'TimeDeposit', 'Loan'].map(c =>
                this.transactionsClient.accountTransactionsGetAll({ category: c })
            )
        );
        const [deposits, timeDeposits, loans] = responses.map(r => r.data.map(a => new Account(a)));
        return { deposits, timeDeposits, loans };
    }

    public async getAccountsCoreData(accountIds: number[]) {
        const response = await this.transactionsClient.accountTransactionsGetAccountsCoreData({
            accountIds,
            includeCollectedBalance: false,
            includePermissions: true,
        });
        return response.data;
    }

    public async getAccountWithTransactionsInDateRange(
        accountId: number,
        accountType: string,
        fromDate: string,
        toDate: string
    ) {
        const accountResponse = await this.transactionsClient.accountTransactionsGetByDateRange(
            accountId,
            accountType,
            { fromDate, toDate }
        );
        const account = accountResponse.data;
        const transactions = account.transactions || [];
        const dateRange = getDateRange(new Date(fromDate), new Date(toDate));
        const balanceHistory = [];

        let { currentBalance } = account;
        for (let i = 0; i < dateRange.length; i++) {
            const dailyBalance = {
                date: '',
                currentBalance: 0,
            };
            const [currentDate] = dateRange[i].split('T');
            const dayTransactions = transactions.filter(
                transaction => transaction.date.split('T')[0] === currentDate
            );
            if (dayTransactions.length > 0) {
                currentBalance =
                    (account.type === 'Loan'
                        ? dayTransactions[dayTransactions.length - 1].balance
                        : dayTransactions[0].runningBalance) || 0;
            }

            dailyBalance.date = currentDate;
            dailyBalance.currentBalance = currentBalance;
            balanceHistory.push(dailyBalance);
        }

        if (!account.id) {
            account.id = accountId;
        }
        account.balanceHistory = balanceHistory;
        return new Account(account);
    }

    public async getTransaction(accountId: number, accountType: string, transactionId: number) {
        const response = await this.transactionsClient.accountTransactionsGet(accountId, {
            type: accountType,
        });
        return new Account(response.data).getTransaction(transactionId);
    }

    public async getCheckImages(checkImageNumber: string, type: 'check' | 'deposit') {
        if (type === 'check') {
            const response = await this.checkImagesClient.checkImagesGetCheckImage({
                checkImageNumber,
            });
            return [new CheckImage(response.data)];
        }

        const response = await this.checkImagesClient.checkImagesGetDepositSet({
            depositImageNumber: checkImageNumber,
        });
        return response.data.map(d => new CheckImage(d));
    }

    /**
     *
     * Research transactions calls
     */

    public async researchTransactions(filters: ResearchTransactionFilterModelDto) {
        const response = await this.researchTransactionsClient.researchTransactionsPost(filters);
        return response.data.map(t => new ResearchedTransaction(t));
    }

    public async getTransactionCodeGroups() {
        const response =
            (await this.transactionCodeGroupClient.transactionCodeGroupGetAll()) as unknown as {
                data: TransactionCode[];
            };
        return response.data.map((code: TransactionCode) => ({ type: code.name }));
    }

    public async getAllAccounts() {
        const request = (await TmHttpClient.getInstance()).request<CompanyAccountModelDto[]>(
            `companyaccounts/GetWithProductFeature?type=&productfeaturetype=All`,
            {
                method: 'GET',
            }
        );
        const response = await request;
        return response.map(
            (acc: CompanyAccountModelDto) =>
                new CompanyAccount(acc as unknown as CompanyAccountModelDto)
        );
    }

    public async getTimeDepositAccountDetails(accountId: number) {
        const response = await this.dashboardClient.dashboardGetTimeDepositTransactionDetail({
            accountId,
        });
        return new TimeDepositAccountDetail(response.data);
    }

    public async getLoanAccountDetails(accountId: number) {
        const response = await this.dashboardClient.dashboardGetLoanTransactionDetail({
            accountId,
        });
        return new LoanAccountDetail(response.data);
    }

    public async getAccountNicknames() {
        const response = await this.companyAccountsClient.companyAccountsGetAccountNicknames();
        return response.data.map((acc: AccountSummaryModelDto) => new Account(acc));
    }

    public async updateAccount(account: Account) {
        if (!account.id) {
            throw new Error('Account ID is required for update');
        }
        const response = await this.companyAccountsClient.companyAccountUpdate(
            account.id,
            account.toDto()
        );
        return new Account(response.data);
    }

    public async updateAccountNicknames(accounts: Account[]) {
        const dtos = accounts.map(acc => acc.toDto());
        const response = await this.companyAccountsClient.companyAccountNickNamesUpdate(dtos);
        return response.data.map((acc: AccountSummaryModelDto) => new Account(acc));
    }
}
